import config from '../config/env';
import { APIError, NetworkError, TimeoutError } from './errors';

class APIClient {
    constructor() {
        this.baseURL = config.BACKEND_URL;
        this.timeout = config.API_TIMEOUT;
        this.defaultHeaders = {
            'Content-Type': 'application/json',
        };
    }

    // Set authentication token
    setAuthToken(token) {
        this.defaultHeaders.Authorization = `Bearer ${token}`;
    }

    // Remove authentication token
    removeAuthToken() {
        delete this.defaultHeaders.Authorization;
    }

    // Create request with timeout
    async makeRequest(url, options = {}) {
        const controller = new AbortController();

        let timeoutId = null;
        if (options?.timeout) {
            timeoutId = setTimeout(() => controller.abort(), options.timeout);
        } else {
            timeoutId = setTimeout(() => controller.abort(), this.timeout);
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers: {
                    ...this.defaultHeaders,
                    ...options.headers,
                },
                signal: controller.signal,
            });

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            if (!response.ok) {
                // Try to parse JSON error, but handle blob responses
                const contentType = response.headers.get('content-type');
                let errorData = {};
                if (contentType && contentType.includes('application/json')) {
                    errorData = await response.json().catch(() => ({}));
                }
                throw new APIError(
                    errorData.message || `HTTP ${response.status}: ${response.statusText}`,
                    response.status,
                    errorData
                );
            }

            return response;
        } catch (error) {
            clearTimeout(timeoutId);

            if (error.name === 'AbortError') {
                throw new TimeoutError('Request timed out');
            }

            if (error instanceof APIError) {
                throw error;
            }

            throw new NetworkError('Network request failed');
        }
    }

    // GET request
    async get(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const response = await this.makeRequest(url, {
            method: 'GET',
            ...options,
        });

        if (options.responseType === 'blob') {
            return response.blob();
        }

        return response.json();
    }

    // POST request
    async post(endpoint, data, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const response = await this.makeRequest(url, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options,
        });

        if (options.responseType === 'blob') {
            return response.blob();
        }

        return response.json();
    }

    // PUT request
    async put(endpoint, data, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const response = await this.makeRequest(url, {
            method: 'PUT',
            body: JSON.stringify(data),
            ...options,
        });

        return response.json();
    }

    // PATCH request
    async patch(endpoint, data, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const response = await this.makeRequest(url, {
            method: 'PATCH',
            body: JSON.stringify(data),
            ...options,
        });

        return response.json();
    }

    // DELETE request
    async delete(endpoint, data = null, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const response = await this.makeRequest(url, {
            method: 'DELETE',
            ...(data ? { body: JSON.stringify(data) } : {}),
            ...options,
        });

        // Handle empty responses
        const text = await response.text();
        return text ? JSON.parse(text) : {};
    }

    // Upload file
    async uploadFile(endpoint, file, additionalData = {}, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const formData = new FormData();

        formData.append('file', file);

        // Add additional data
        Object.keys(additionalData).forEach(key => {
            formData.append(key, additionalData[key]);
        });

        const response = await this.makeRequest(url, {
            method: 'POST',
            body: formData,
            headers: {
                // Don't set Content-Type for FormData, let browser set it
                ...this.defaultHeaders,
                ...options.headers,
            },
            ...options,
        });

        return response.json();
    }
}

// Create and export a singleton instance
const apiClient = new APIClient();
export default apiClient;
